import React, { useEffect, useRef, useState } from 'react';
import './MarketplaceSellerProfile.scss';
import { useDispatch } from 'react-redux';
import ButtonLoader from '../Layout/Buttons/ButtonLoader';
import { GetStatesByRegion } from '../../slices/locations-slice/thunks/get-states-by-region.thunk';
import SellerLegalDocsInfoInputForm, {
    SellerLegalDocsInfoInputFormRef,
} from './SellerLegalDocsInfoInputForm/SellerLegalDocsInfoInputForm';
import SellerMyProfileForm, {
    SellerMyProfileFormRef,
} from './SellerMyProfileForm/SellerMyProfileForm';
import PreviewProfileModal from './PreviewProfileModal/PreviewProfileModal';
import { useStateSelector } from '../../store/selectors';
import { MarketplaceSellerProfile } from '../../slices/profiles-data-slice/models/marketplace-seller-profile';
import { useCurrentMarketplaceParty } from '../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';

enum SellerProfileTab {
    MyProfile = 1,
    LegalDocsInfo = 2,
}

const SellerProfileTabs = [
    {
        id: SellerProfileTab.MyProfile,
        content: 'My Profile',
    },
    {
        id: SellerProfileTab.LegalDocsInfo,
        content: 'Legal Docs Info',
    },
] as const;

export const MarketplaceSellerProfileComponent = () => {
    const [activeTab, setActiveTab] = useState<SellerProfileTab>(
        SellerProfileTab.MyProfile
    );
    const sellerMyProfileFormRef = useRef<SellerMyProfileFormRef>(null);
    const sellerLegalDocsInfoFormRef =
        useRef<SellerLegalDocsInfoInputFormRef>(null);
    const dispatch = useDispatch() as any;

    const [isSaving, setIsSaving] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);

    const sellerProfile = useStateSelector(
        (state) => state.profilesData.selectedPofile
    ) as MarketplaceSellerProfile;

    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const currentParty = useCurrentMarketplaceParty(currentEngagement?.id);
    const isBroker = currentParty === TimelineParty.Broker;

    useEffect(() => {
        dispatch(GetStatesByRegion());
    }, []);

    const renderTabContent = (): React.ReactNode => {
        const isEditable = !isBroker;

        switch (activeTab) {
            case SellerProfileTab.MyProfile:
                return (
                    <SellerMyProfileForm
                        ref={sellerMyProfileFormRef}
                        setIsSaving={setIsSaving}
                        isEditable={isEditable}
                    />
                );
            case SellerProfileTab.LegalDocsInfo:
                return (
                    <SellerLegalDocsInfoInputForm
                        ref={sellerLegalDocsInfoFormRef}
                        setIsSaving={setIsSaving}
                        isEditable={isEditable}
                    />
                );
            default:
                return null;
        }
    };

    const renderActionButtons = (): React.ReactNode => (
        <div className="actions-buttons mt-4">
            {!isBroker && (
                <ButtonLoader
                    className="btn btn-primary submit-button"
                    isLoading={isSaving}
                    loaderButtonText={''}
                    disabled={isSaving}
                    buttonText={'Save Changes'}
                    onClick={() => {
                        if (
                            activeTab === SellerProfileTab.MyProfile &&
                            sellerMyProfileFormRef.current
                        ) {
                            sellerMyProfileFormRef.current.handleSubmit();
                        } else if (
                            activeTab === SellerProfileTab.LegalDocsInfo &&
                            sellerLegalDocsInfoFormRef.current
                        ) {
                            sellerLegalDocsInfoFormRef.current.handleSubmit();
                        }
                    }}></ButtonLoader>
            )}
            <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                    setIsPreviewModalVisible(true);
                }}>
                Preview Profile
            </button>
        </div>
    );

    return (
        <div className="marketplace-seller-profile">
            <div className="body_inner_content marketplace-seller-tabs-container">
                <div className="RRT__container">
                    <div className="RRT__tabs">
                        {SellerProfileTabs.map((item) => (
                            <div
                                key={item.id}
                                className={
                                    activeTab === item.id
                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                        : 'RRT__tab'
                                }
                                onClick={() => setActiveTab(item.id)}>
                                {item.content}
                            </div>
                        ))}
                        {renderActionButtons()}
                    </div>
                </div>
                {renderTabContent()}
            </div>
            {isPreviewModalVisible && (
                <PreviewProfileModal
                    isVisible={isPreviewModalVisible}
                    onClose={() => setIsPreviewModalVisible(false)}
                    profileData={sellerProfile}
                />
            )}
        </div>
    );
};
